import { findJitenSubtitle } from '@project/common/jiten/reader-events';
import type { MutableRefObject } from 'react';
import React, { useEffect, useLayoutEffect, useState, useMemo, useCallback, useRef, useImperativeHandle } from 'react';
import { makeStyles } from '@mui/styles';
import type { Theme } from '@mui/material';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import type {
    AudioTrackModel,
    CardModel,
    CopySubtitleMessage,
    CardTextFieldValues,
    IndexedSubtitleModel,
    PlaybackState,
    SubtitleModel,
    AssSubtitleTrack,
    DisplaySubtitleModel,
    VideoTabModel,
} from '@project/common';
import type { JitenTarget } from '@project/common/jiten/jiten';
import { useJitenTarget } from '@project/common/app/hooks/use-jiten-target';
import { readJitenWords } from '@project/common/jiten/reader-words';
import { useJitenSubtitleSource } from '@project/common/app/hooks/use-jiten-subtitles';
import { AutoPausePreference, PlayMode, PostMinePlayback, PostMineAction } from '@project/common';
import type { AsbplayerSettings, SettingsProvider } from '@project/common/settings';
import { isTrackAutoCopyable, VideoSubtitleSplitBehavior } from '@project/common/settings';
import { SubtitleCollection } from '@project/common/subtitle-collection';
import type { SubtitleReader } from '@project/common/subtitle-reader';
import { assContentForRendering } from '@project/common/subtitle-reader/ass-content';
import type { KeyBinder, ShortcutEvent } from '@project/common/key-binder';
import { asbTrace } from '@project/common/util/log';
import {
    clampMediaTimestamp,
    download,
    formatAsSignedMs,
    surroundingSubtitles,
    timeDurationDisplay,
} from '@project/common/util';
import BroadcastChannelVideoProtocol from '@project/common/app/services/broadcast-channel-video-protocol';
import ChromeTabVideoProtocol from '@project/common/app/services/chrome-tab-video-protocol';
import Clock from '@project/common/playback/timing/clock';
import type { Point } from '@project/common/app/components/Controls';
import Controls from '@project/common/app/components/Controls';
import Grid from '@mui/material/Grid';
import Fade from '@mui/material/Fade';
import Slide from '@mui/material/Slide';
import { isMobile } from 'react-device-detect';
import MediaAdapter from '@project/common/app/services/media-adapter';
import SubtitlePlayer, { minSubtitlePlayerWidth } from '@project/common/app/components/SubtitlePlayer';
import VideoChannel from '@project/common/app/services/video-channel';
import type ChromeExtension from '@project/common/app/services/chrome-extension';
import type PlaybackPreferenceController from '@project/common/playback/controllers/playback-preference-controller';
import { useAppBarHeight } from '@project/common/hooks/use-app-bar-height';
import { createBlobUrl } from '@project/common/blob-url';
import type { MiningContext } from '@project/common/app/services/mining-context';
import type { SeekTimestampCommand, WebSocketClient } from '@project/common/web-socket-client';
import {
    resolveVideoSubtitleSplitLayout,
    useVideoAspectRatio,
} from '@project/common/app/components/video-subtitle-split';
import type { FileWithId } from '@project/common/file-selector';
import AnimationFrameTimingDriver from '@project/common/playback/timing/animation-frame-timing-driver';
import PlaybackEngine from '@project/common/playback/playback-engine';
import { playbackModeNotificationJoin } from '@project/common/playback/controllers/playback-mode-controller';
import {
    buildPlaybackTimelineExportPlan,
    playbackTimelineSettingsSummary,
    playbackTimelineToHtml,
} from '@project/common/playback/timeline/playback-timeline-html';
import type {
    PlaybackTimelineModeLabels,
    PlaybackTimelineOptionLabels,
} from '@project/common/playback/timeline/playback-timeline-html';
import { createTheme } from '@project/common/theme/theme';
import Alert from '@project/common/app/components/Alert';
import type { AlertNotification } from '@project/common/app/components/Alert';
import useSnackbar from '@project/common/hooks/use-snackbar';

const minVideoPlayerWidth = 300;
const subtitleCollectionOptions = { returnLastShown: true, returnNextToShow: true, showingCheckRadiusMs: 150 };
interface StylesProps {
    appBarHidden: boolean;
    appBarHeight: number;
    bottomPanelHeight: number;
}

const useStyles = makeStyles<Theme, StylesProps>((theme) => ({
    root: ({ appBarHidden, appBarHeight, bottomPanelHeight }) => ({
        height: `calc(100dvh - ${appBarHidden ? 0 : appBarHeight}px - ${bottomPanelHeight}px)`,
        position: 'relative',
        overflowX: 'hidden',
        transition: theme.transitions.create(['padding-bottom', 'height'], { duration: 200 }),
    }),
    container: {
        width: '100%',
        height: '100%',
    },
    videoContainer: {
        width: '100%',
        height: '100%',
        border: 0,
        display: 'block',
    },
}));

function trackLengthMs(videoDuration: number | undefined, subtitles: SubtitleModel[] | undefined): number {
    let subtitlesLength;
    if (subtitles && subtitles.length > 0) {
        subtitlesLength = subtitles[subtitles.length - 1].originalEnd;
    } else {
        subtitlesLength = 0;
    }

    const videoLength = videoDuration ? 1000 * videoDuration : 0;
    return Math.max(videoLength, subtitlesLength);
}

function subtitlesForPlayer<T extends IndexedSubtitleModel>(subtitles: readonly T[]): T[] {
    return subtitles.map((subtitle) => ({ ...subtitle }));
}

function pause(clock: Clock, mediaAdapter: MediaAdapter, forwardToMedia: boolean) {
    clock.stop();

    if (forwardToMedia) {
        mediaAdapter.pause();
    }
}

export interface MediaSources {
    subtitleFiles: FileWithId[];
    flattenSubtitleFiles?: boolean;
    videoFile?: FileWithId;
    videoFileUrl?: string;
}

interface PlayerProps {
    sources?: MediaSources;
    subtitles: DisplaySubtitleModel[];
    mediaId?: string;
    subtitleReader: SubtitleReader;
    settingsProvider: SettingsProvider;
    settings: AsbplayerSettings;
    onSettingsChanged?: (settings: Partial<AsbplayerSettings>) => void;
    profile?: string;
    playbackPreferences: PlaybackPreferenceController;
    keyBinder: KeyBinder;
    extension: ChromeExtension;
    videoContainerRef?: MutableRefObject<HTMLDivElement | null>;
    renderVideo?: (
        videoFile: string,
        channel: string,
        disableKeyEvents: boolean,
        subtitles: DisplaySubtitleModel[],
        assTracks: readonly AssSubtitleTrack[]
    ) => React.ReactNode;
    videoChannelRef?: MutableRefObject<VideoChannel | null>;
    drawerOpen: boolean;
    bottomPanelHeight?: number;
    appBarHidden: boolean;
    showCopyButton: boolean;
    videoFullscreen: boolean;
    hideSubtitlePlayer: boolean;
    videoPopOut: boolean;
    tab?: VideoTabModel;
    availableTabs: VideoTabModel[];
    miningContext: MiningContext;
    origin: string;
    onError: (error: any) => void;
    onUnloadVideo: (url: string) => void;
    onCopy: (card: CardModel, postMineAction: PostMineAction | undefined, id: string | undefined) => void;
    onLoaded: (file: File[]) => void;
    onTabSelected: (tab: VideoTabModel) => void;
    onAnkiDialogRequest: () => void;
    onAnkiDialogRewind: () => void;
    onHideSubtitlePlayer: () => void;
    onVideoPopOut: () => void;
    onSubtitles: React.Dispatch<React.SetStateAction<DisplaySubtitleModel[] | undefined>>;
    onLoadFiles?: () => void;
    onLoadSubtitles?: () => void;
    subtitleExtractionProgress?: number;
    onCancelSubtitleExtraction?: () => void;
    disableKeyEvents: boolean;
    jumpToSubtitle?: SubtitleModel;
    onJumpToSubtitleHandled?: () => void;
    rewindSubtitle?: SubtitleModel;
    hideControls?: boolean;
    forceCompressedMode?: boolean;
    webSocketClient?: WebSocketClient;
    playbackTimelineFileName?: string;
    playbackTimelineModeLabels: PlaybackTimelineModeLabels;
    playbackTimelineOptionLabels: PlaybackTimelineOptionLabels;
}

export interface PlayerRef {
    downloadSubtitleTimeline: () => void;
}

const Player = React.memo(React.forwardRef<PlayerRef, PlayerProps>(PlayerComponent));

function PlayerComponent(
    {
        sources,
        subtitles,
        mediaId,
        subtitleReader,
        settingsProvider,
        settings,
        onSettingsChanged,
        profile,
        playbackPreferences,
        keyBinder,
        extension,
        videoContainerRef,
        renderVideo,
        videoChannelRef,
        drawerOpen,
        bottomPanelHeight = 0,
        appBarHidden,
        showCopyButton,
        videoFullscreen,
        hideSubtitlePlayer,
        videoPopOut,
        tab,
        availableTabs,
        miningContext,
        origin,
        onError,
        onUnloadVideo,
        onCopy,
        onLoaded,
        onTabSelected,
        onAnkiDialogRequest,
        onHideSubtitlePlayer,
        onVideoPopOut,
        onSubtitles,
        onLoadFiles,
        onLoadSubtitles,
        subtitleExtractionProgress,
        onCancelSubtitleExtraction,
        disableKeyEvents: parentDisableKeyEvents,
        jumpToSubtitle,
        onJumpToSubtitleHandled,
        rewindSubtitle,
        hideControls,
        forceCompressedMode,
        webSocketClient,
        playbackTimelineFileName,
        playbackTimelineModeLabels,
        playbackTimelineOptionLabels,
    }: PlayerProps,
    ref: React.ForwardedRef<PlayerRef>
) {
    const { t } = useTranslation();
    const [playModes, setPlayModes] = useState<Set<PlayMode>>(() => new Set([PlayMode.normal]));
    const playModesRef = useRef<Set<PlayMode>>(playModes);
    playModesRef.current = playModes;
    const [subtitlesSentThroughChannel, setSubtitlesSentThroughChannel] = useState<boolean>();
    const [assTracks, setAssTracks] = useState<readonly AssSubtitleTrack[]>([]);
    const subtitlesRef = useRef<DisplaySubtitleModel[]>(undefined);
    subtitlesRef.current = subtitles;
    const [subtitleCollection, setSubtitleCollection] = useState<SubtitleCollection<DisplaySubtitleModel>>(
        SubtitleCollection.empty<DisplaySubtitleModel>()
    );
    const subtitleCollectionRef = useRef<SubtitleCollection<DisplaySubtitleModel>>(subtitleCollection);
    subtitleCollectionRef.current = subtitleCollection;

    const subtitleFiles = sources?.subtitleFiles;
    const flattenSubtitleFiles = sources?.flattenSubtitleFiles;
    const videoFile = sources?.videoFile;
    const videoFileUrl = sources?.videoFileUrl;
    const playbackPositionKey = videoFile?.file.name;
    const syntheticPlayback = videoFileUrl === undefined && tab === undefined;
    useJitenSubtitleSource(subtitles, tab === undefined, settings.assSubtitleStyles);
    const [videoFocused, setVideoFocused] = useState(false);
    const disableKeyEvents = parentDisableKeyEvents || (videoFocused && Boolean(videoFileUrl && !videoPopOut));
    const playModeEnabled = subtitles && subtitles.length > 0 && Boolean(videoFileUrl);
    const [subtitlePlayerResizing, setSubtitlePlayerResizing] = useState<boolean>(false);
    const [loadingSubtitles, setLoadingSubtitles] = useState<boolean>(false);
    const [lastJumpToTopTimestamp, setLastJumpToTopTimestamp] = useState<number>(0);
    const [offset, setOffset] = useState<number>(0);
    const [playbackRate, setPlaybackRate] = useState<number>(settings.playbackRate);
    const [alert, setAlert] = useState<{ open: boolean; notifications: AlertNotification[] }>({
        open: false,
        notifications: [],
    });
    const [audioTracks, setAudioTracks] = useState<AudioTrackModel[]>();
    const [selectedAudioTrack, setSelectedAudioTrack] = useState<string>();
    const [channelId, setChannelId] = useState<string>();
    const [channel, setChannel] = useState<VideoChannel>();
    const videoDurationRef = useRef<number>(0);
    const channelRef = useRef<VideoChannel>(undefined);
    channelRef.current = channel;
    const [wasPlayingWhenMiningStarted, setWasPlayingWhenMiningStarted] = useState<boolean>();
    const hideSubtitlePlayerRef = useRef<boolean>(undefined);
    hideSubtitlePlayerRef.current = hideSubtitlePlayer;
    const [disabledSubtitleTracks, setDisabledSubtitleTracks] = useState<{ [track: number]: boolean }>({});
    useEffect(() => {
        asbTrace('playback/player', 'Subtitle list track display state', { disabledSubtitleTracks });
    }, [disabledSubtitleTracks]);
    const mousePositionRef = useRef<Point>({ x: 0, y: 0 });
    const mediaAdapter = useMemo(() => {
        asbTrace('playback/player', 'Creating media adapter', {
            hasChannel: channel !== undefined,
            hasVideoFileUrl: videoFileUrl !== undefined,
            hasTab: tab !== undefined,
        });
        if (videoFileUrl || tab) {
            return new MediaAdapter({ current: channel });
        }

        return new MediaAdapter({ current: undefined });
    }, [channel, videoFileUrl, tab]);
    const clock = useMemo<Clock>(() => new Clock(() => performance.now()), []);
    const clockRef = useRef<Clock>(clock);
    clockRef.current = clock;
    const syntheticPlaybackEngineRef = useRef<PlaybackEngine<DisplaySubtitleModel>>(undefined);
    const profileRef = useRef(profile);
    const applyOffsetRef = useRef<(offset: number, forwardToVideo: boolean) => void>(undefined);
    const [pendingPlaybackPosition, setPendingPlaybackPosition] = useState<number>();
    const resumePlaybackSnackbar = useSnackbar({
        open: pendingPlaybackPosition !== undefined,
        onClose: () => syntheticPlaybackEngineRef.current?.dismissPlaybackPosition(),
    });
    const [playbackState, setPlaybackState] = useState<PlaybackState>();
    const appBarHeight = useAppBarHeight();
    const classes = useStyles({ appBarHidden, appBarHeight, bottomPanelHeight });
    const calculateLengthMs = (videoDurationRef: MutableRefObject<number>, playerSubtitles = subtitlesRef.current) =>
        trackLengthMs(videoDurationRef.current, playerSubtitles);

    const handleDownloadSubtitleTimeline = useCallback(() => {
        const displaySubtitles = subtitlesRef.current ?? [];
        const timelineSettingsSummary = playbackTimelineSettingsSummary(settings, playbackTimelineOptionLabels);
        const timelineTracks = [...new Set(displaySubtitles.map((subtitle) => subtitle.track))]
            .sort((left, right) => left - right)
            .map((track) => ({
                track,
                label: playbackTimelineOptionLabels.subtitleTrack(track + 1),
            }));
        const selectedTrack = timelineTracks[0]?.track;
        const playbackSubtitles =
            selectedTrack === undefined ? [] : displaySubtitles.filter((subtitle) => subtitle.track === selectedTrack);
        const currentPlayModes = syntheticPlaybackEngineRef.current?.playbackModes ?? playModesRef.current;
        const playbackPlan = buildPlaybackTimelineExportPlan({
            subtitles: playbackSubtitles,
            durationMs: calculateLengthMs(videoDurationRef, displaySubtitles),
            settings,
            playbackRate,
        });
        const title = playbackTimelineFileName ?? 'Subtitle playback timeline';
        download(
            new Blob(
                [
                    playbackTimelineToHtml({
                        plan: playbackPlan,
                        themeColor: createTheme(settings.themeType).palette.primary.main,
                        title,
                        modeLabels: playbackTimelineModeLabels,
                        timelineOptionsTitle: timelineSettingsSummary.title,
                        timelineOptions: timelineSettingsSummary.options,
                        timelineSettings: timelineSettingsSummary.settings,
                        timelineTracks,
                        initialModeVisibility: {
                            normal: currentPlayModes.has(PlayMode.normal),
                            fastForward: currentPlayModes.has(PlayMode.fastForward),
                            condensed: currentPlayModes.has(PlayMode.condensed),
                            autoPauseAtStart:
                                currentPlayModes.has(PlayMode.autoPause) &&
                                settings.autoPausePreference !== AutoPausePreference.atEnd,
                            autoPauseAtEnd:
                                currentPlayModes.has(PlayMode.autoPause) &&
                                settings.autoPausePreference !== AutoPausePreference.atStart,
                            repeat: currentPlayModes.has(PlayMode.repeat),
                        },
                        timelineSubtitles: displaySubtitles,
                    }),
                ],
                { type: 'text/html' }
            ),
            `${title}.html`
        );
    }, [playbackRate, playbackTimelineFileName, playbackTimelineModeLabels, playbackTimelineOptionLabels, settings]);

    useImperativeHandle(ref, () => ({ downloadSubtitleTimeline: handleDownloadSubtitleTimeline }), [
        handleDownloadSubtitleTimeline,
    ]);

    const seek = useCallback(
        async (time: number, clock: Clock, forwardToMedia: boolean, options: { readonly paused: boolean }) => {
            const clampedTime = clampMediaTimestamp(time, (channelRef.current?.duration ?? 0) * 1000);
            clock.setTime(clampedTime, options);

            if (forwardToMedia) {
                await mediaAdapter.seek(clampedTime / 1000);
            }
        },
        [mediaAdapter]
    );

    const handleSubtitlePlayerResizeStart = useCallback(() => setSubtitlePlayerResizing(true), []);
    const handleSubtitlePlayerResizeEnd = useCallback(
        (width: number) => {
            setSubtitlePlayerResizing(false);

            if (settings.videoSubtitleSplitBehavior === VideoSubtitleSplitBehavior.rememberSplitPosition) {
                playbackPreferences.subtitlePlayerWidth = width;
            }
        },
        [playbackPreferences, settings.videoSubtitleSplitBehavior]
    );

    const updatePlaybackRate = useCallback(
        (playbackRate: number, forwardToMedia: boolean) => {
            if (clock.rate !== playbackRate) {
                clock.rate = playbackRate;
                setPlaybackRate(playbackRate);
                if (forwardToMedia) mediaAdapter.playbackRate(playbackRate);
            }
        },
        [clock, mediaAdapter]
    );

    const notifyOffset = useCallback((offset: number, key: string) => {
        setAlert({
            open: true,
            notifications: [{ key, message: formatAsSignedMs(offset), severity: 'info' }],
        });
    }, []);

    const notifyPlaybackRate = useCallback(
        (options: ReturnType<PlaybackEngine<DisplaySubtitleModel>['playbackRateChanged']>) => {
            if (!options?.notify) return;
            setAlert({
                open: true,
                notifications: [
                    {
                        key: options.notification.key,
                        message: {
                            locKey: options.notification.locKey,
                            replacements: options.notification.replacements,
                        },
                        severity: 'info',
                    },
                ],
            });
        },
        []
    );

    const synchronizePlaybackModes = useCallback((modes: ReadonlySet<PlayMode>) => {
        const synchronizedModes = new Set(modes);
        playModesRef.current = synchronizedModes;
        setPlayModes(synchronizedModes);
    }, []);

    useEffect(() => {
        if (!syntheticPlayback) {
            setPlaybackState(undefined);
            return;
        }

        asbTrace('playback/player', 'Creating synthetic playback owner', {
            subtitleCount: subtitlesRef.current?.length ?? 0,
            hasPlaybackPositionKey: playbackPositionKey !== undefined,
            appIntegration: extension.supportsAppIntegration,
        });
        const playbackEngine = new PlaybackEngine({
            settingsProvider,
            appIntegration: extension.supportsAppIntegration,
            autoPauseCorrectionDisabled: false,
            subtitles: subtitlesRef.current ?? [],
            playbackModesDisabled: true,
            playbackModesSuppressed: false,
            playbackPositionKeys: playbackPositionKey ? [playbackPositionKey] : [],
            timingDriver: new AnimationFrameTimingDriver({
                paused: () => !clock.running,
                durationMs: () => trackLengthMs(undefined, subtitlesRef.current),
                currentTimeMs: () => clock.time({ maxMs: Number.POSITIVE_INFINITY }),
                playbackRate: () => clock.rate,
                requestAnimationFrameCallback: (callback) => requestAnimationFrame(callback),
                cancelAnimationFrameCallback: (handle) => cancelAnimationFrame(handle),
                addEventListener: (type, listener) => {
                    switch (type) {
                        case 'play':
                            clock.onEvent('start', listener);
                            break;
                        case 'pause':
                            clock.onEvent('stop', listener);
                            break;
                        case 'seeked':
                            clock.onEvent('settime', listener);
                            break;
                        case 'timeupdate':
                            clock.onEvent('timeupdate', listener);
                            break;
                    }
                },
                removeEventListener: (type, listener) => {
                    switch (type) {
                        case 'play':
                            clock.removeEvent('start', listener);
                            break;
                        case 'pause':
                            clock.removeEvent('stop', listener);
                            break;
                        case 'seeked':
                            clock.removeEvent('settime', listener);
                            break;
                        case 'timeupdate':
                            clock.removeEvent('timeupdate', listener);
                            break;
                    }
                },
            }),
            callbacks: {
                pause: () => clock.stop(),
                play: async () => {
                    clock.start();
                },
                seek: async (timestampMs) => {
                    clock.setTime(timestampMs, { paused: !clock.running });
                },
                setPlaybackRate: (rate) => updatePlaybackRate(rate, false),
                setSubtitleOffset: (offset, options, notificationKey) => {
                    applyOffsetRef.current?.(offset, false);
                    if (options.notifyPlayer) notifyOffset(offset, notificationKey);
                },
                playbackStateChanged: setPlaybackState,
                playbackPositionChanged: setPendingPlaybackPosition,
                saveSettings: (settings) => {
                    if (onSettingsChanged !== undefined) onSettingsChanged(settings);
                    else void settingsProvider.set(settings).catch(onError);
                },
                playbackModesChanged: ({ modes }) => synchronizePlaybackModes(modes),
                initialPlaybackSettingsChanged: (settings) => {
                    asbTrace('playback/player', 'Applying initial synthetic playback settings', {
                        subtitleOffset: settings.subtitleOffset,
                        playbackRate: settings.playbackRate,
                        playbackModes: [...settings.playbackModeTransition.modes],
                    });
                    const notifications = settings.notifications.offsetAndRate.map((notification) =>
                        notification.type === 'message'
                            ? notification.message
                            : t(notification.notification.locKey, notification.notification.replacements)
                    );
                    synchronizePlaybackModes(settings.playbackModeTransition.modes);
                    if (!notifications.length) return;
                    setAlert({
                        open: true,
                        notifications: [
                            {
                                message: notifications.join(playbackModeNotificationJoin),
                                severity: 'info',
                                autoHideDuration: settings.autoHideDuration,
                            },
                        ],
                    });
                },
                onError: (error) => {
                    asbTrace('playback/player', 'Synthetic playback owner reported an error', { error });
                    onError(error);
                },
            },
        });
        syntheticPlaybackEngineRef.current = playbackEngine;
        asbTrace('playback/player', 'Binding synthetic playback owner');
        playbackEngine.bind();

        return () => {
            asbTrace('playback/player', 'Unbinding synthetic playback owner');
            playbackEngine.unbind();
            if (syntheticPlaybackEngineRef.current === playbackEngine) {
                syntheticPlaybackEngineRef.current = undefined;
            }
        };
    }, [
        clock,
        extension,
        onError,
        onSettingsChanged,
        playbackPositionKey,
        settingsProvider,
        syntheticPlayback,
        t,
        notifyOffset,
        synchronizePlaybackModes,
        updatePlaybackRate,
    ]);

    useEffect(() => {
        if (profileRef.current === profile) return;
        profileRef.current = profile;
        syntheticPlaybackEngineRef.current?.profileChanged(profile);
    }, [profile]);

    useEffect(() => {
        if (!syntheticPlayback) return;
        syntheticPlaybackEngineRef.current?.settingsChanged(settings);
    }, [settings, syntheticPlayback]);

    useEffect(() => {
        if (!syntheticPlayback) return;
        syntheticPlaybackEngineRef.current?.subtitlesChanged(subtitles);
    }, [subtitles, syntheticPlayback]);

    useEffect(() => {
        if (syntheticPlayback || !tab || subtitles.length === 0) return;
        setOffset(subtitles[0].start - subtitles[0].originalStart);
    }, [subtitles, syntheticPlayback, tab]);

    const applyOffset = useCallback(
        (offset: number, forwardToVideo: boolean) => {
            setOffset(offset);
            if (!subtitles) {
                return;
            }

            const length = subtitles.length > 0 ? subtitles[subtitles.length - 1].end + offset : 0;

            const newSubtitles = subtitles.map((s, i) => ({
                ...s,
                start: s.originalStart + offset,
                end: s.originalEnd + offset,
                displayTime: timeDurationDisplay(s.originalStart + offset, length),
                displayEndTime: timeDurationDisplay(s.originalEnd + offset, length),
                index: i,
            }));

            if (forwardToVideo) {
                if (channel !== undefined) {
                    channel.offset(offset);

                    // Older versions of extension don't support the offset message
                    if (tab !== undefined && extension.installed && !extension.supportsOffsetMessage) {
                        channel.subtitles(newSubtitles, subtitleFiles?.map((f) => f.file.name) ?? ['']);
                    }
                }
            }

            onSubtitles(newSubtitles);
        },
        [subtitleFiles, subtitles, extension, tab, channel, onSubtitles]
    );
    applyOffsetRef.current = applyOffset;

    useEffect(() => {
        if (!videoFile && !tab) {
            return;
        }

        let channel: VideoChannel;
        setPlaybackState(undefined);

        asbTrace('playback/player', 'Creating media channel', {
            source: videoFile ? 'file' : 'tab',
            hasVideoFile: videoFile !== undefined,
            hasTab: tab !== undefined,
        });

        if (videoFile) {
            const channelId = uuidv4();
            channel = new VideoChannel(new BroadcastChannelVideoProtocol(channelId));
            setChannelId(channelId);
            onLoaded([videoFile.file]);
        } else {
            channel = new VideoChannel(new ChromeTabVideoProtocol(tab!.id, tab!.src, extension));
            channel.init();
        }

        if (videoChannelRef) {
            videoChannelRef.current = channel;
        }

        setChannel(channel);

        return () => {
            asbTrace('playback/player', 'Closing media channel');
            setPlaybackState(undefined);
            clock.setTime(0, { paused: true });
            channel.close();
        };
    }, [clock, videoPopOut, videoFile, tab, extension, videoChannelRef, onLoaded]);

    useEffect(() => {
        async function init() {
            asbTrace('playback/player', 'Loading player subtitles', {
                subtitleFileCount: subtitleFiles?.length ?? 0,
                flattenSubtitleFiles,
            });
            const offset = syntheticPlaybackEngineRef.current?.lastSubtitleOffset ?? 0;
            let subtitles: DisplaySubtitleModel[] | undefined;

            if (subtitleFiles !== undefined && subtitleFiles.length > 0) {
                setLoadingSubtitles(true);

                try {
                    const [nodes, assTracks] = await Promise.all([
                        subtitleReader.subtitles(
                            subtitleFiles.map((f) => f.file),
                            flattenSubtitleFiles
                        ),
                        Promise.all(
                            subtitleFiles.flatMap(({ file }, track) =>
                                tab === undefined && file.name.endsWith('.ass')
                                    ? [
                                          file.text().then((content) => ({
                                              track: flattenSubtitleFiles ? 0 : track,
                                              content: assContentForRendering(content),
                                          })),
                                      ]
                                    : []
                            )
                        ),
                    ]);
                    setAssTracks(assTracks);
                    const length = nodes.length > 0 ? nodes[nodes.length - 1].end + offset : 0;

                    subtitles = nodes.map((s, i) => ({
                        ...s,
                        start: s.start + offset,
                        originalStart: s.start,
                        end: s.end + offset,
                        originalEnd: s.end,
                        displayTime: timeDurationDisplay(s.start + offset, length),
                        displayEndTime: timeDurationDisplay(s.end + offset, length),
                        index: i,
                    }));

                    setSubtitlesSentThroughChannel(false);
                    asbTrace('playback/player', 'Loaded player subtitles', {
                        subtitleCount: subtitles.length,
                        offset,
                    });
                    onSubtitles(subtitles);
                } catch (e) {
                    setAssTracks([]);
                    asbTrace('playback/player', 'Failed to load player subtitles', { error: e });
                    onError(e);
                    onSubtitles([]);
                } finally {
                    setLoadingSubtitles(false);
                }
            } else {
                setAssTracks([]);
                subtitles = undefined;
            }
        }

        void init()
            .then(() => onLoaded(subtitleFiles?.map((f) => f.file) ?? []))
            .catch(onError);
    }, [subtitleReader, onLoaded, onError, subtitleFiles, flattenSubtitleFiles, onSubtitles, tab]);

    useEffect(() => {
        if (tab) {
            const newCol = new SubtitleCollection<DisplaySubtitleModel>(subtitleCollectionOptions);
            newCol.setSubtitles(subtitlesRef.current ?? []);
            setSubtitleCollection(newCol);
            subtitleCollectionRef.current = newCol;
            return; // Handled by extension
        }
        if (!tab && !mediaId) return;
        const newCol = new SubtitleCollection<DisplaySubtitleModel>(subtitleCollectionOptions);
        newCol.setSubtitles(subtitlesRef.current ?? []);
        setSubtitleCollection(newCol);
        subtitleCollectionRef.current = newCol;
    }, [mediaId, tab]);

    useEffect(() => {
        if (!subtitleCollectionRef.current) return;
        subtitleCollectionRef.current.setSubtitles(subtitles);
    }, [subtitles]);

    useEffect(() => {
        return channel?.onSubtitlesUpdated((updatedSubtitles) => {
            onSubtitles((prevSubtitles) => {
                if (!prevSubtitles?.length) return prevSubtitles;
                const allSubtitles = prevSubtitles.slice();
                for (const s of subtitlesForPlayer(updatedSubtitles)) {
                    allSubtitles[s.index] = { ...allSubtitles[s.index], ...s };
                }
                return allSubtitles;
            });
        });
    }, [channel, onSubtitles]);

    useEffect(() => {
        setSubtitlesSentThroughChannel(false);
    }, [channel]);

    useEffect(
        () => channel?.onExit(() => videoFileUrl && onUnloadVideo(videoFileUrl)),
        [channel, onUnloadVideo, videoFileUrl]
    );
    useEffect(() => channel?.onPopOutToggle(() => onVideoPopOut()), [channel, onVideoPopOut]);
    useEffect(() => channel?.onHideSubtitlePlayerToggle(onHideSubtitlePlayer), [channel, onHideSubtitlePlayer]);
    useEffect(
        () =>
            channel?.onReady(() => {
                asbTrace('playback/player', 'Media channel reported ready', {
                    durationMs: channel.duration * 1000,
                    subtitleCount: subtitles.length,
                    hasVideoFileName: videoFile?.file?.name !== undefined,
                });
                videoDurationRef.current = channel.duration;
                return channel?.ready(
                    calculateLengthMs(videoDurationRef, subtitles),
                    videoFile?.file?.name,
                    videoFile?.file
                );
            }),
        [channel, subtitles, videoFile]
    );
    useEffect(() => {
        if (
            channel === undefined ||
            (renderVideo !== undefined && !videoPopOut) ||
            subtitles === undefined ||
            subtitlesSentThroughChannel ||
            subtitleFiles === undefined ||
            subtitleFiles.length === 0
        ) {
            return;
        }

        return channel.onReady(() => {
            setSubtitlesSentThroughChannel(true);
            asbTrace('playback/player', 'Sending subtitles to media channel', {
                subtitleCount: subtitles.length,
                fileCount: flattenSubtitleFiles ? 1 : subtitleFiles.length,
            });
            channel.subtitles(
                subtitles,
                flattenSubtitleFiles ? [subtitleFiles[0].file.name] : subtitleFiles.map((f) => f.file.name),
                assTracks
            );
        });
    }, [
        subtitles,
        channel,
        flattenSubtitleFiles,
        subtitleFiles,
        subtitlesSentThroughChannel,
        renderVideo,
        assTracks,
        videoPopOut,
    ]);
    useEffect(
        () =>
            channel?.onReady(() => {
                asbTrace('playback/player', 'Sending subtitle settings to media channel');
                channel?.subtitleSettings(settings);
            }),
        [channel, settings]
    );
    useEffect(
        () => channel?.onReady(() => channel?.hideSubtitlePlayerToggle(hideSubtitlePlayer)),
        [channel, hideSubtitlePlayer]
    );
    useEffect(() => channel?.ankiSettings(settings), [channel, settings]);
    useEffect(() => channel?.miscSettings(settings), [channel, settings]);
    useEffect(
        () =>
            channel?.onReady(() => {
                if (channel?.audioTracks && channel?.audioTracks?.length > 1) {
                    setAudioTracks(channel?.audioTracks);
                    setSelectedAudioTrack(channel?.selectedAudioTrack);
                } else {
                    setAudioTracks(undefined);
                    setSelectedAudioTrack(undefined);
                }
            }),
        [channel]
    );
    useEffect(
        () =>
            channel?.onReady((paused) => {
                asbTrace('playback/player', 'Media channel initial playback state', {
                    paused,
                    currentTimeMs: channel.currentTime * 1000,
                    playbackRate: channel.playbackRate,
                });
                if (channel) {
                    clock.setTime(channel.currentTime * 1000, { paused });
                }

                if (channel?.playbackRate) {
                    clock.rate = channel.playbackRate;
                    setPlaybackRate(channel.playbackRate);
                }
            }),
        [channel, clock]
    );
    useEffect(() => {
        return channel?.onPlaybackState((state) => {
            setPlaybackState(state);
            clock.setTime(state.timestampMs, { paused: state.paused });
        });
    }, [channel, clock]);
    useEffect(
        () =>
            channel?.onDuration(() => {
                videoDurationRef.current = channel.duration;
            }),
        [channel]
    );
    const play = useCallback((clock: Clock, mediaAdapter: MediaAdapter, forwardToMedia: boolean) => {
        clock.start();

        if (forwardToMedia) {
            mediaAdapter.play();
        }
    }, []);

    useEffect(
        () => channel?.onPlay((forwardToMedia) => play(clock, mediaAdapter, forwardToMedia)),
        [channel, mediaAdapter, clock, play]
    );
    useEffect(
        () => channel?.onPause((forwardToMedia) => pause(clock, mediaAdapter, forwardToMedia)),
        [channel, mediaAdapter, clock]
    );
    useEffect(() => {
        return channel?.onOffset((offset) => applyOffset(offset, false));
    }, [channel, applyOffset]);
    useEffect(() => channel?.onPlaybackRate(updatePlaybackRate), [channel, updatePlaybackRate]);
    useEffect(
        () =>
            channel?.onCopy(
                (
                    subtitle,
                    surroundingSubtitles,
                    cardTextFieldValues,
                    audio,
                    image,
                    url,
                    postMineAction,
                    id,
                    mediaTimestamp
                ) =>
                    onCopy(
                        {
                            subtitle,
                            surroundingSubtitles,
                            subtitleFileName: subtitle ? (subtitleFiles?.[subtitle.track]?.file?.name ?? '') : '',
                            ...cardTextFieldValues,
                            mediaTimestamp: mediaTimestamp ?? 0,
                            file: videoFile
                                ? {
                                      name: videoFile.file.name,
                                      blobUrl: createBlobUrl(videoFile.file),
                                      audioTrack: channel?.selectedAudioTrack,
                                      playbackRate: channel?.playbackRate,
                                  }
                                : undefined,
                            audio,
                            image,
                            url,
                        },
                        postMineAction,
                        id
                    )
            ),
        [channel, onCopy, videoFile, subtitleFiles]
    );
    useEffect(() => {
        if (channel === undefined) return;

        const unsubscribe = channel.onPlayModes((playModes) => {
            playModesRef.current = playModes;
            setPlayModes(playModes);
        });
        const playModes = channel.playModes;
        playModesRef.current = playModes;
        setPlayModes(playModes);
        return unsubscribe;
    }, [channel]);
    useEffect(() => {
        return channel?.onCurrentTime((currentTime, forwardToMedia) => {
            void (async () => {
                const playing = clock.running;
                await seek(currentTime * 1000, clock, forwardToMedia, { paused: true });
                if (playing) clock.start();
            })();
        });
    }, [channel, clock, seek]);
    useEffect(
        () =>
            channel?.onAudioTrackSelected((id) => {
                void (async () => {
                    const playing = clock.running;

                    if (playing) {
                        clock.stop();
                    }

                    await mediaAdapter.onReady();
                    if (playing) {
                        clock.start();
                    }

                    setSelectedAudioTrack(id);
                })();
            }),
        [channel, clock, mediaAdapter]
    );
    useEffect(() => channel?.onAnkiDialogRequest(() => onAnkiDialogRequest()), [channel, onAnkiDialogRequest]);
    useEffect(
        () =>
            channel?.onToggleSubtitleTrackInList((track) =>
                setDisabledSubtitleTracks((tracks) => {
                    const newTracks = { ...tracks };
                    newTracks[track] = !tracks[track];
                    return newTracks;
                })
            ),
        [channel]
    );
    useEffect(() => channel?.onLoadFiles(() => onLoadFiles?.()), [channel, onLoadFiles]);
    useEffect(() => channel?.onLoadSubtitles(() => onLoadSubtitles?.()), [channel, onLoadSubtitles]);
    useEffect(() => {
        return miningContext.onEvent('stopped-mining', () => {
            switch (settings.postMiningPlaybackState) {
                case PostMinePlayback.play:
                    play(clock, mediaAdapter, true);
                    break;
                case PostMinePlayback.pause:
                    pause(clock, mediaAdapter, true);
                    break;
                case PostMinePlayback.remember:
                    if (wasPlayingWhenMiningStarted) {
                        play(clock, mediaAdapter, true);
                    }
                    break;
            }
        });
    }, [miningContext, settings, wasPlayingWhenMiningStarted, clock, mediaAdapter, play]);

    useEffect(() => {
        return miningContext.onEvent('started-mining', () => {
            if (clock.running) {
                pause(clock, mediaAdapter, true);
                setWasPlayingWhenMiningStarted(true);
            } else {
                setWasPlayingWhenMiningStarted(false);
            }
        });
    }, [miningContext, clock, mediaAdapter]);

    useEffect(() => {
        if (videoPopOut && videoFileUrl && channelId) {
            // shortcut: popout has its own Reader context; one request across windows needs Reader support.
            window.open(
                origin + '?video=' + encodeURIComponent(videoFileUrl) + '&channel=' + channelId + '&popout=true',
                'asbplayer-video-' + videoFileUrl,
                'resizable,width=800,height=450'
            );
        }

        setLastJumpToTopTimestamp(Date.now());
    }, [videoPopOut, channelId, videoFileUrl, videoContainerRef, videoChannelRef, origin]);

    const handlePlay = useCallback(() => play(clock, mediaAdapter, true), [clock, mediaAdapter, play]);
    const handlePause = useCallback(() => pause(clock, mediaAdapter, true), [clock, mediaAdapter]);
    const handleSeek = useCallback(
        async (progress: number) => {
            const playing = clock.running;
            await seek(progress * calculateLengthMs(videoDurationRef), clock, true, { paused: true });

            if (playing) {
                clock.start();
            }
        },
        [clock, seek]
    );

    const handleSeekToTimestamp = useCallback(
        async (time: number, shouldPlay: boolean) => {
            if (!shouldPlay) {
                pause(clock, mediaAdapter, true);
            }

            await seek(time, clock, true, { paused: shouldPlay ? !clock.running : true });

            if (shouldPlay && !clock.running) {
                // play method will start the clock again
                play(clock, mediaAdapter, true);
            }
        },
        [clock, seek, play, mediaAdapter]
    );

    const handleCopyFromSubtitlePlayer = useCallback(
        async (
            subtitle: SubtitleModel,
            surroundingSubtitles: SubtitleModel[],
            postMineAction: PostMineAction,
            forceUseGivenSubtitle?: boolean,
            cardTextFieldValues?: CardTextFieldValues
        ) => {
            if (
                settings.miningDestination === 'jiten' &&
                (postMineAction === PostMineAction.mineNewJitenWords ||
                    postMineAction === PostMineAction.showAnkiDialog)
            ) {
                const words = readJitenWords(subtitle, surroundingSubtitles);
                cardTextFieldValues = { ...cardTextFieldValues, jitenWords: words };
            }
            if (videoFileUrl) {
                if (forceUseGivenSubtitle) {
                    channel?.copy(postMineAction, subtitle, surroundingSubtitles, cardTextFieldValues);
                } else {
                    // Let VideoPlayer do the copying to ensure copied subtitle is consistent with the VideoPlayer clock
                    channel?.copy(postMineAction);
                }
            } else {
                onCopy(
                    {
                        subtitle,
                        surroundingSubtitles,
                        subtitleFileName: subtitleFiles?.[subtitle.track]?.file?.name ?? '',
                        mediaTimestamp: clock.time({ maxMs: calculateLengthMs(videoDurationRef) }),
                        file:
                            videoFile === undefined
                                ? undefined
                                : {
                                      name: videoFile.file.name,
                                      audioTrack: selectedAudioTrack,
                                      playbackRate,
                                      blobUrl: createBlobUrl(videoFile.file),
                                  },
                        ...cardTextFieldValues,
                    },
                    postMineAction,
                    undefined
                );
            }
        },
        [
            channel,
            onCopy,
            clock,
            videoFile,
            videoFileUrl,
            subtitleFiles,
            selectedAudioTrack,
            playbackRate,
            settings.miningDestination,
        ]
    );

    useJitenTarget(
        useCallback(
            (target: JitenTarget) => {
                if (settings.miningDestination !== 'jiten' || !settings.jitenAutoAttach || disableKeyEvents) return;
                const timestamp = clock.time({ maxMs: calculateLengthMs(videoDurationRef) });
                const subtitle = findJitenSubtitle(target, subtitlesRef.current ?? [], timestamp);
                if (!subtitle) return;
                const surrounding = surroundingSubtitles(
                    subtitlesRef.current!,
                    subtitle.index,
                    settings.surroundingSubtitlesCountRadius,
                    settings.surroundingSubtitlesTimeRadius
                );
                if (tab) {
                    const message: CopySubtitleMessage = {
                        command: 'copy-subtitle',
                        postMineAction: PostMineAction.updateLastCard,
                        subtitle,
                        surroundingSubtitles: surrounding,
                        jitenTarget: target,
                    };
                    extension.sendMessageToVideoElement(message, tab.id, tab.src);
                } else {
                    void handleCopyFromSubtitlePlayer(subtitle, surrounding, PostMineAction.updateLastCard, true, {
                        jitenTarget: target,
                        miningDestination: 'jiten',
                    });
                }
            },
            [settings, disableKeyEvents, clock, handleCopyFromSubtitlePlayer, tab, extension]
        )
    );

    const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        mousePositionRef.current.x = e.screenX;
        mousePositionRef.current.y = e.screenY;
    }, []);

    const handleAudioTrackSelected = useCallback((id: string) => channel?.audioTrackSelected(id), [channel]);

    const handleOffsetChange = useCallback(
        (offset: number) => {
            if (syntheticPlaybackEngineRef.current) {
                syntheticPlaybackEngineRef.current.subtitleOffsetChanged(offset, { notifyPlayer: true });
                return;
            }

            applyOffset(offset, true);
        },
        [applyOffset]
    );

    const handlePlaybackRateChange = useCallback(
        (playbackRate: number) => {
            if (syntheticPlaybackEngineRef.current) {
                notifyPlaybackRate(syntheticPlaybackEngineRef.current.playbackRateChanged(playbackRate));
                return;
            }
            updatePlaybackRate(playbackRate, true);
        },
        [notifyPlaybackRate, updatePlaybackRate]
    );

    const handlePlayMode = useCallback(
        (targetMode: PlayMode) => {
            channel?.playMode(targetMode);
        },
        [channel]
    );

    const handleToggleSubtitleTrack = useCallback(
        (track: number) =>
            setDisabledSubtitleTracks((tracks) => {
                const newTracks = { ...tracks };
                newTracks[track] = !tracks[track];
                return newTracks;
            }),
        []
    );

    const handleSubtitlesHighlighted = useCallback(
        (subtitles: SubtitleModel[]) => {
            if (subtitles.length === 0 || !settings.autoCopyCurrentSubtitle || !document.hasFocus()) {
                return;
            }

            const text = subtitles
                .filter((s) => isTrackAutoCopyable(settings.autoCopyableTracks, s.track))
                .map((s) => s.text)
                .join('\n');

            if (text) {
                navigator.clipboard.writeText(text).catch(() => {
                    // ignore
                });
            }
        },
        [settings.autoCopyCurrentSubtitle, settings.autoCopyableTracks]
    );

    useEffect(() => {
        if (tab) {
            return;
        }

        const interval = setInterval(() => {
            void (async () => {
                const progress = clock.progress({ durationMs: calculateLengthMs(videoDurationRef) });

                if (progress >= 1) {
                    pause(clock, mediaAdapter, true);
                }
            })();
        }, 1000);

        return () => clearInterval(interval);
    }, [clock, mediaAdapter, seek, tab]);

    useEffect(() => {
        const unbind = keyBinder.bindPlay(
            (event) => {
                event.preventDefault();

                if (clock.running) {
                    pause(clock, mediaAdapter, true);
                } else {
                    play(clock, mediaAdapter, true);
                }
            },
            () => disableKeyEvents
        );

        return () => unbind();
    }, [keyBinder, clock, mediaAdapter, disableKeyEvents, play]);

    useEffect(() => {
        return keyBinder.bindAdjustPlaybackRate(
            (event, increase) => {
                event.preventDefault();
                if (increase) {
                    handlePlaybackRateChange(playbackRate + settings.speedChangeStep);
                } else {
                    handlePlaybackRateChange(playbackRate - settings.speedChangeStep);
                }
            },
            () => disableKeyEvents
        );
    }, [handlePlaybackRateChange, playbackRate, settings.speedChangeStep, disableKeyEvents, keyBinder]);

    const togglePlayMode = useCallback(
        (event: ShortcutEvent, targetMode: PlayMode) => {
            if (!playModeEnabled) return;
            event.preventDefault();
            channel?.playMode(targetMode);
        },
        [playModeEnabled, channel]
    );

    useEffect(() => {
        return keyBinder.bindAutoPause(
            (event) => togglePlayMode(event, PlayMode.autoPause),
            () => disableKeyEvents
        );
    }, [togglePlayMode, keyBinder, disableKeyEvents]);

    useEffect(() => {
        return keyBinder.bindCondensedPlayback(
            (event) => togglePlayMode(event, PlayMode.condensed),
            () => disableKeyEvents
        );
    }, [togglePlayMode, keyBinder, disableKeyEvents]);

    useEffect(() => {
        return keyBinder.bindFastForwardPlayback(
            (event) => togglePlayMode(event, PlayMode.fastForward),
            () => disableKeyEvents
        );
    }, [togglePlayMode, keyBinder, disableKeyEvents]);

    useEffect(() => {
        return keyBinder.bindToggleRepeat(
            (event) => togglePlayMode(event, PlayMode.repeat),
            () => disableKeyEvents
        );
    }, [keyBinder, disableKeyEvents, togglePlayMode]);

    useEffect(() => {
        return keyBinder.bindCycleAutoPauseResumeMode(
            (event) => {
                event.preventDefault();
                const notification = syntheticPlaybackEngineRef.current?.cycleAutoPauseResumeMode();
                if (notification === undefined) return;
                setAlert({
                    open: true,
                    notifications: [
                        {
                            key: notification.key,
                            message: {
                                locKey: notification.locKey,
                                replacements: { value: t(notification.valueLocKey) },
                            },
                            severity: 'info',
                        },
                    ],
                });
            },
            () => disableKeyEvents || !playModeEnabled
        );
    }, [disableKeyEvents, keyBinder, playModeEnabled, t]);

    const handleSubtitleStudyToggle = useCallback(
        (mode: 'blur' | 'hide') => {
            const key = mode === 'blur' ? 'subtitleStudyBlurEnabled' : 'subtitleStudyHideEnabled';
            onSettingsChanged?.({ [key]: !settings[key] });
        },
        [onSettingsChanged, settings]
    );

    useEffect(() => {
        return keyBinder.bindToggleSubtitleStudy(
            (event, mode) => {
                event.preventDefault();
                handleSubtitleStudyToggle(mode);
            },
            () => disableKeyEvents || !onSettingsChanged
        );
    }, [keyBinder, handleSubtitleStudyToggle, disableKeyEvents, onSettingsChanged]);

    useEffect(() => {
        return keyBinder.bindToggleSubtitleVisibility(
            (event) => {
                event.preventDefault();
                const notification = syntheticPlaybackEngineRef.current?.toggleSubtitleVisibility();
                if (notification === undefined) return;
                setAlert({
                    open: true,
                    notifications: [
                        {
                            key: notification.key,
                            message: {
                                locKey: notification.locKey,
                                replacements: { value: t(notification.valueLocKey) },
                            },
                            severity: 'info',
                        },
                    ],
                });
            },
            () => disableKeyEvents || !playModeEnabled
        );
    }, [disableKeyEvents, keyBinder, playModeEnabled, t]);

    useEffect(() => channel?.fullscreenToggle(videoFullscreen), [channel, videoFullscreen]);

    useEffect(() => {
        if (rewindSubtitle?.start === undefined) {
            return;
        }

        pause(clock, mediaAdapter, true);

        void seek(rewindSubtitle.start, clock, true, { paused: true });
    }, [clock, rewindSubtitle?.start, mediaAdapter, seek]);

    useEffect(() => {
        if (!webSocketClient) {
            return;
        }

        webSocketClient.onSeekTimestamp = async ({ body: { timestamp } }: SeekTimestampCommand) => {
            void seek(timestamp * 1000, clock, true, { paused: !clock.running });
        };
    }, [webSocketClient, extension, seek, clock]);

    const playerLayoutRef = useRef<HTMLDivElement>(null);
    const subtitlePanelRef = useRef<HTMLDivElement>(null);
    const [fullscreenSubtitlePanelOpen, setFullscreenSubtitlePanelOpen] = useState(false);
    const fullscreenSubtitleHoverEnabled =
        videoFullscreen && !isMobile && Boolean(videoFileUrl && !videoPopOut && subtitles?.length);
    useEffect(() => {
        if (!fullscreenSubtitleHoverEnabled) {
            return;
        }

        const layout = playerLayoutRef.current!;
        let open = false;
        let resumePlayback = false;
        const closePanel = () => {
            if (!open) {
                return;
            }

            open = false;
            setFullscreenSubtitlePanelOpen(false);
            if (resumePlayback) {
                handlePlay();
            }
        };
        const handlePointerMove = (event: PointerEvent) => {
            if (event.pointerType !== 'mouse') {
                return;
            }

            const boundary = open
                ? layout.getBoundingClientRect().right - subtitlePanelRef.current!.offsetWidth
                : layout.getBoundingClientRect().right - 24;
            if (event.clientX >= boundary) {
                if (!open) {
                    open = true;
                    resumePlayback = clock.running;
                    handlePause();
                    setFullscreenSubtitlePanelOpen(true);
                }
            } else {
                closePanel();
            }
        };
        layout.addEventListener('pointermove', handlePointerMove);
        layout.addEventListener('pointerleave', closePanel);
        return () => {
            layout.removeEventListener('pointermove', handlePointerMove);
            layout.removeEventListener('pointerleave', closePanel);
            closePanel();
        };
    }, [fullscreenSubtitleHoverEnabled, videoFileUrl, clock, handlePlay, handlePause]);
    const [{ width: playerWidth, height: playerHeight }, setPlayerSize] = useState({ width: 0, height: 0 });
    useLayoutEffect(() => {
        const observer = new ResizeObserver(([entry]) => {
            setPlayerSize({ width: entry.contentRect.width, height: entry.contentRect.height });
        });
        observer.observe(playerLayoutRef.current!);
        return () => observer.disconnect();
    }, []);

    const videoInWindow = Boolean(videoFileUrl && !videoPopOut);
    const shouldLoadVideoAspectRatio = videoInWindow;
    const videoAspectRatio = useVideoAspectRatio(videoFileUrl, shouldLoadVideoAspectRatio);
    const loaded = videoFileUrl || (sources?.subtitleFiles.length ?? 0) > 0 || tab;
    const aspectFitVideoWidth = videoAspectRatio
        ? Math.max(minVideoPlayerWidth, Math.round(playerHeight * videoAspectRatio))
        : undefined;
    const autoSubtitlePlayerInitialWidth =
        videoInWindow && aspectFitVideoWidth !== undefined ? Math.max(0, playerWidth - aspectFitVideoWidth) : undefined;
    const subtitlePlayerInitialWidth = resolveVideoSubtitleSplitLayout({
        behavior: settings.videoSubtitleSplitBehavior,
        persistedWidth: playbackPreferences.subtitlePlayerWidth,
        autoWidth: autoSubtitlePlayerInitialWidth,
    });
    const horizontalSubtitlePlayerWidth = Math.max(
        minSubtitlePlayerWidth,
        subtitlePlayerInitialWidth ?? Math.max(350, playerWidth * 0.25)
    );
    const horizontalVideoWidth = Math.max(0, playerWidth - horizontalSubtitlePlayerWidth);
    const verticalVideoHeight = Math.max(0, playerHeight - minSubtitlePlayerWidth);
    const videoArea = (width: number, height: number) => {
        if (videoAspectRatio === undefined) {
            return 0;
        }

        const displayedWidth = Math.min(width, height * videoAspectRatio);
        return displayedWidth * (displayedWidth / videoAspectRatio);
    };
    const verticalLayout =
        videoInWindow &&
        !videoFullscreen &&
        (playerWidth < minVideoPlayerWidth + minSubtitlePlayerWidth ||
            (videoAspectRatio !== undefined &&
                videoArea(playerWidth, verticalVideoHeight) > videoArea(horizontalVideoWidth, playerHeight)));
    const subtitlePlayerMaxResizeWidth = Math.max(0, playerWidth - minVideoPlayerWidth);
    const notEnoughSpaceForSubtitlePlayer = subtitlePlayerMaxResizeWidth < minSubtitlePlayerWidth;
    const showSubtitleLoadingState = verticalLayout && loadingSubtitles;
    const actuallyHideSubtitlePlayer =
        videoInWindow &&
        ((videoFullscreen ? !fullscreenSubtitlePanelOpen : hideSubtitlePlayer) ||
            (!showSubtitleLoadingState &&
                (!subtitles ||
                    subtitles.length === 0 ||
                    (!videoFullscreen && !verticalLayout && notEnoughSpaceForSubtitlePlayer))));
    const handleAlertClosed = useCallback(() => setAlert((alert) => ({ ...alert, open: false })), []);
    const SubtitlePanelTransition = videoFullscreen ? Slide : Fade;

    return (
        <div
            onMouseMove={handleMouseMove}
            className={classes.root}
            style={{
                paddingBottom:
                    subtitleExtractionProgress !== undefined ? 'calc(52px + env(safe-area-inset-bottom))' : undefined,
                boxSizing: 'border-box',
            }}
        >
            <Alert
                open={resumePlaybackSnackbar.open}
                useAppLogo={false}
                onClose={resumePlaybackSnackbar.close}
                onMouseEnter={resumePlaybackSnackbar.onMouseEnter}
                onMouseLeave={resumePlaybackSnackbar.onMouseLeave}
                autoHideDuration={0}
                disableAutoHide={true}
                severity="info"
                anchor="top"
            >
                {t('info.resumePlaybackPrompt', {
                    time: timeDurationDisplay(pendingPlaybackPosition ?? 0, pendingPlaybackPosition ?? 0, false),
                })}
                <Button
                    size="small"
                    color="inherit"
                    style={{ pointerEvents: 'auto', marginLeft: 12 }}
                    onClick={() => void syntheticPlaybackEngineRef.current?.resumePlaybackPosition()}
                >
                    {t('info.resumePlaybackButton')}
                </Button>
            </Alert>
            <Alert
                open={alert.open}
                onClose={handleAlertClosed}
                notifications={alert.notifications}
                useAppLogo={false}
                anchor="top"
            />
            <Grid
                ref={playerLayoutRef}
                container
                direction={verticalLayout ? 'column' : 'row'}
                wrap="nowrap"
                className={classes.container}
            >
                {videoInWindow && channelId && (
                    <Grid
                        item
                        style={{
                            flexGrow: verticalLayout && !actuallyHideSubtitlePlayer ? 0 : 1,
                            flexBasis: verticalLayout ? 'auto' : undefined,
                            minWidth: verticalLayout ? 0 : minVideoPlayerWidth,
                            minHeight: verticalLayout ? 0 : undefined,
                            width: verticalLayout ? '100%' : undefined,
                            aspectRatio:
                                verticalLayout && !actuallyHideSubtitlePlayer
                                    ? (videoAspectRatio ?? 16 / 9)
                                    : undefined,
                            position: 'relative',
                        }}
                    >
                        <div
                            ref={videoContainerRef}
                            className={classes.videoContainer}
                            onPointerDownCapture={() => setVideoFocused(true)}
                            onFocusCapture={() => setVideoFocused(true)}
                            onBlurCapture={(event) => {
                                if (!event.currentTarget.contains(event.relatedTarget)) setVideoFocused(false);
                            }}
                            style={{ pointerEvents: subtitlePlayerResizing ? 'none' : 'auto' }}
                        >
                            {renderVideo?.(
                                videoFileUrl!,
                                channelId,
                                parentDisableKeyEvents || !videoFocused,
                                subtitles,
                                assTracks
                            )}
                        </div>
                    </Grid>
                )}

                <SubtitlePanelTransition direction="left" in={!actuallyHideSubtitlePlayer} timeout={100} appear>
                    <Grid
                        ref={subtitlePanelRef}
                        item
                        hidden={!videoFullscreen && actuallyHideSubtitlePlayer}
                        onPointerDownCapture={() => setVideoFocused(false)}
                        onFocusCapture={() => setVideoFocused(false)}
                        style={{
                            ...(videoFullscreen
                                ? {
                                      position: 'absolute',
                                      top: 0,
                                      right: 0,
                                      height: '100%',
                                      zIndex: 20,
                                      maxWidth: '100%',
                                  }
                                : {}),
                            flexGrow: verticalLayout ? 1 : videoInWindow ? 0 : 1,
                            flexShrink: verticalLayout ? 1 : 0,
                            width: videoFullscreen
                                ? Math.max(300, playerWidth * 0.2)
                                : verticalLayout
                                  ? '100%'
                                  : 'auto',
                            minWidth: verticalLayout ? 0 : undefined,
                            minHeight: verticalLayout ? 0 : undefined,
                        }}
                    >
                        {loaded && !(videoFileUrl && !videoPopOut) && !hideControls && (
                            <Controls
                                mousePositionRef={mousePositionRef}
                                clock={clock}
                                length={calculateLengthMs(videoDurationRef)}
                                displayLength={calculateLengthMs(videoDurationRef, subtitles)}
                                audioTracks={audioTracks}
                                selectedAudioTrack={selectedAudioTrack}
                                tabs={(!videoFileUrl && availableTabs) || undefined}
                                selectedTab={tab}
                                offsetEnabled={true}
                                offset={offset}
                                playbackRate={playbackRate}
                                playbackRateEnabled={!tab || extension.supportsPlaybackRateMessage}
                                onPlaybackRateChange={handlePlaybackRateChange}
                                playModeEnabled={playModeEnabled}
                                playModes={playModes}
                                onPlay={handlePlay}
                                onPause={handlePause}
                                onSeek={handleSeek}
                                onAudioTrackSelected={handleAudioTrackSelected}
                                onTabSelected={onTabSelected}
                                onOffsetChange={handleOffsetChange}
                                onPlayMode={handlePlayMode}
                                onLoadSubtitles={onLoadSubtitles}
                                subtitleExtractionProgress={subtitleExtractionProgress}
                                onCancelSubtitleExtraction={onCancelSubtitleExtraction}
                                disableKeyEvents={disableKeyEvents}
                                playbackPreferences={playbackPreferences}
                                showOnMouseMovement={true}
                                subtitleStudyBlurEnabled={settings.subtitleStudyBlurEnabled}
                                subtitleStudyHideEnabled={settings.subtitleStudyHideEnabled}
                                onSubtitleStudyToggle={onSettingsChanged && handleSubtitleStudyToggle}
                                previewEnabled={false}
                            />
                        )}
                        <SubtitlePlayer
                            subtitles={subtitles}
                            subtitleCollection={subtitleCollection}
                            playbackState={playbackState}
                            clock={clock}
                            extension={extension}
                            length={calculateLengthMs(videoDurationRef)}
                            jumpToSubtitle={jumpToSubtitle}
                            onJumpToSubtitleHandled={onJumpToSubtitleHandled}
                            drawerOpen={drawerOpen}
                            appBarHidden={appBarHidden}
                            compressed={videoInWindow || (forceCompressedMode ?? false)}
                            resizable={videoInWindow && !verticalLayout && !videoFullscreen}
                            verticalLayout={verticalLayout}
                            showCopyButton={showCopyButton}
                            loading={loadingSubtitles}
                            displayHelp={(videoPopOut && videoFile?.file?.name) || undefined}
                            disableKeyEvents={disableKeyEvents}
                            // On later versions of the extension, VideoPlayer will receive the mining commands instead
                            disableMiningBinds={extension.supportsVideoPlayerMiningCommands && videoFile !== undefined}
                            lastJumpToTopTimestamp={lastJumpToTopTimestamp}
                            hidden={actuallyHideSubtitlePlayer}
                            disabledSubtitleTracks={disabledSubtitleTracks}
                            onSeek={handleSeekToTimestamp}
                            onCopy={handleCopyFromSubtitlePlayer}
                            onOffsetChange={handleOffsetChange}
                            onToggleSubtitleTrack={handleToggleSubtitleTrack}
                            onSubtitlesHighlighted={handleSubtitlesHighlighted}
                            onResizeStart={handleSubtitlePlayerResizeStart}
                            onResizeEnd={handleSubtitlePlayerResizeEnd}
                            maxResizeWidth={subtitlePlayerMaxResizeWidth}
                            settings={settings}
                            keyBinder={keyBinder}
                            webSocketClient={webSocketClient}
                            initialWidth={subtitlePlayerInitialWidth}
                        />
                    </Grid>
                </SubtitlePanelTransition>
            </Grid>
        </div>
    );
}

export default Player;
